import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell, LogOut, Search, User, LogIn } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { loginHref } from "@/lib/authReturn";
import { TID } from "@/lib/testIds";

export default function Header() {
  const { user, logout, isGuest } = useAuth();
  const navigate = useNavigate();

  const displayName = isGuest ? "Guest" : (user?.name || "Member");
  const initials = (isGuest ? "GU" : (user?.name || "SS"))
    .split(" ")
    .map((s) => s[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header
      data-testid={TID.shell.header}
      className="fixed top-0 right-0 left-0 md:left-64 z-30 h-16 glass border-b border-white/10 flex items-center px-6 gap-4"
    >
      <div className="flex-1 max-w-md">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
          <Input
            placeholder="Search songs, people, opportunities…"
            data-testid="global-search-input"
            className="pl-9 bg-matte-card border-white/10 focus-visible:border-gold focus-visible:ring-gold/40 rounded-none h-9 text-white placeholder:text-white/40"
          />
        </div>
      </div>
      {isGuest ? (
        <>
          <div
            data-testid="header-guest-badge"
            className="hidden sm:flex items-center gap-2 h-9 px-3 border border-gold/30 bg-gold/[0.03]"
          >
            <span className="h-6 w-6 rounded-full border border-gold/50 text-gold text-[10px] font-display font-semibold flex items-center justify-center">GU</span>
            <span className="text-xs font-display uppercase tracking-widest text-gold">Guest</span>
          </div>
          <Button
            data-testid="header-signin"
            onClick={() => navigate(loginHref())}
            className="rounded-none h-9 px-4 bg-goldSheen text-black border border-gold/60 font-display uppercase tracking-widest text-xs"
          >
            <LogIn className="h-3.5 w-3.5 mr-2" /> Sign In
          </Button>
        </>
      ) : (
        <>
          <Link
            to="/app/notifications"
            aria-label="Notifications"
            data-testid="header-notifications"
            className="relative h-9 w-9 flex items-center justify-center border border-white/10 hover:border-gold text-white/70 hover:text-gold transition-colors"
          >
            <Bell className="h-4 w-4" />
          </Link>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid={TID.shell.userMenu}
                className="h-9 px-3 flex items-center gap-3 border border-white/10 hover:border-gold transition-colors bg-matte-card"
              >
                <span className="h-6 w-6 rounded-full bg-goldSheen text-black text-[11px] font-display font-semibold flex items-center justify-center">
                  {initials}
                </span>
                <span className="hidden sm:block text-xs font-display uppercase tracking-widest text-white/80">
                  {displayName}
                </span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="w-56 bg-matte-card border-white/10 text-white rounded-none"
            >
              <DropdownMenuLabel className="font-display uppercase tracking-widest text-[10px] text-white/50">
                {user?.email}
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-white/10" />
              <DropdownMenuItem
                className="cursor-pointer focus:bg-white/[0.04] focus:text-gold"
                onClick={() => navigate("/app/profile/view")}
              >
                <User className="mr-2 h-4 w-4" /> Profile
              </DropdownMenuItem>
              <DropdownMenuItem
                className="cursor-pointer focus:bg-white/[0.04] focus:text-gold"
                onClick={() => navigate("/app/settings")}
              >
                Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-white/10" />
              <DropdownMenuItem
                data-testid={TID.shell.logout}
                onClick={handleLogout}
                className="cursor-pointer focus:bg-white/[0.04] focus:text-gold"
              >
                <LogOut className="mr-2 h-4 w-4" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </>
      )}
    </header>
  );
}
